import React from 'react';
import { Container, Row, Col, Badge, Button, Card, Image } from 'react-bootstrap';
import { motion } from 'framer-motion';
import featuresImg from "../../assets/powerfull_tools.jpg";

const HeroSection = () => {
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.2
            }
        }
    }

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.5, ease: "easeOut" }
        }
    }

    const imageVariants = {
        hidden: { opacity: 0, scale: 0.9 },
        visible: {
            opacity: 1,
            scale: 1,
            transition: { duration: 0.6, ease: "easeOut" }
        }
    }

    const badgeVariants = {
        hidden: { opacity: 0, scale: 0.8 },
        visible: {
            opacity: 1,
            scale: 1,
            transition: { duration: 0.4 }
        }
    }

    const statsVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.5, ease: "easeOut" }
        }
    }

    return (
        <section className="min-vh-100 hero-section-primary3 position-relative d-flex align-items-center" id="hero-growth">
            <Container>
                <Row className="align-items-center g-4">
                    <Col lg={6} xs={12} className="order-lg-1 order-2">
                        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }}>
                            <motion.h1 variants={itemVariants} className="display-5 fw-semibold text-primary mb-3">
                                Growth Strategy that scales with your business
                            </motion.h1>
                            <motion.p variants={itemVariants} className="lead text-secondary mb-4">
                                We craft data-informed roadmaps, prioritize the highest ROI opportunities, and help you execute with clarity—so growth becomes predictable, not accidental.
                            </motion.p>
                            <motion.div variants={itemVariants} className="d-flex flex-wrap align-items-center gap-2 mb-4">
                                <motion.div variants={badgeVariants} whileHover={{ scale: 1.05 }}>
                                    <Badge bg="success-subtle" text="success" className="border border-success-subtle">Go-to-Market</Badge>
                                </motion.div>
                                <motion.div variants={badgeVariants} whileHover={{ scale: 1.05 }}>
                                    <Badge bg="primary-subtle" text="primary" className="border border-primary-subtle">North Star Metrics</Badge>
                                </motion.div>
                                <motion.div variants={badgeVariants} whileHover={{ scale: 1.05 }}>
                                    <Badge bg="warning-subtle" text="warning" className="border border-warning-subtle">Experimentation</Badge>
                                </motion.div>
                            </motion.div>
                            <motion.div variants={itemVariants} className="d-sm-flex align-items-center gap-3 mb-4">
                                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                    <Button variant="success" href="#contact">Get a Free Consultation</Button>
                                </motion.div>
                                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                    <Button variant="gradient" href="/index.html#pricing">See Plans</Button>
                                </motion.div>
                            </motion.div>
                            <Row className="g-3">
                                <Col xs={6} md={4}>
                                    <motion.div variants={itemVariants} whileHover={{ y: -5, transition: { duration: 0.2 } }}>
                                        <Card className="shadow-sm h-100">
                                            <Card.Body className="p-3">
                                                <div className="d-flex align-items-center">
                                                    <i className="ri-line-chart-line text-success fs-4 me-2"></i>
                                                    <div>
                                                        <h6 className="mb-0">+3x Leads</h6>
                                                        <small className="text-secondary">avg. in 60 days</small>
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </motion.div>
                                </Col>
                                <Col xs={6} md={4}>
                                    <motion.div variants={itemVariants} whileHover={{ y: -5, transition: { duration: 0.2 } }}>
                                        <Card className="shadow-sm h-100">
                                            <Card.Body className="p-3">
                                                <div className="d-flex align-items-center">
                                                    <i className="ri-focus-3-line text-primary fs-4 me-2"></i>
                                                    <div>
                                                        <h6 className="mb-0">North Star</h6>
                                                        <small className="text-secondary">metric alignment</small>
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </motion.div>
                                </Col>
                                <Col xs={12} md={4}>
                                    <motion.div variants={itemVariants} whileHover={{ y: -5, transition: { duration: 0.2 } }}>
                                        <Card className="shadow-sm h-100">
                                            <Card.Body className="p-3">
                                                <div className="d-flex align-items-center">
                                                    <i className="ri-flask-line text-warning fs-4 me-2"></i>
                                                    <div>
                                                        <h6 className="mb-0">Rapid Tests</h6>
                                                        <small className="text-secondary">weekly experiments</small>
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </motion.div>
                                </Col>
                            </Row>
                        </motion.div>
                    </Col>
                    <Col lg={{ span: 5, offset: 1 }} xs={12} className="order-lg-2 order-1">
                        <motion.div className="position-relative"
                            variants={imageVariants}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.3 }}
                        >
                            <motion.div className="ratio ratio-4x3 rounded-4 overflow-hidden shadow-sm" whileHover={{ scale: 1.02, transition: { duration: 0.3 } }}>
                                <Image src={featuresImg} className="w-100 h-100 object-fit-cover" />
                            </motion.div>
                            <motion.div className="position-absolute ms-2 bottom-0 start-0 translate-middle-y bg-white rounded-3 shadow p-3 d-none d-sm-flex align-items-center"
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.5, duration: 0.5 }}
                                whileHover={{ scale: 1.05 }}
                            >
                                <i className="ri-rocket-2-line text-success fs-4 me-2"></i>
                                <div>
                                    <small className="text-secondary">Time to impact</small>
                                    <h6 className="mb-0">2–4 weeks</h6>
                                </div>
                            </motion.div>
                        </motion.div>
                    </Col>
                </Row>

                <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={containerVariants}>
                    <Row className="g-3 align-items-center mt-1">
                        <Col md={3} xs={6}>
                            <motion.div variants={statsVariants} whileHover={{ y: -5, scale: 1.02, transition: { duration: 0.2 } }}>
                                <Card className="border text-center h-100">
                                    <Card.Body className="p-3">
                                        <h4 className="text-primary mb-0"><span className="me-1">7</span>days</h4>
                                        <small className="text-secondary">to first experiment</small>
                                    </Card.Body>
                                </Card>
                            </motion.div>
                        </Col>
                        <Col md={3} xs={6}>
                            <motion.div variants={statsVariants} whileHover={{ y: -5, scale: 1.02, transition: { duration: 0.2 } }}>
                                <Card className="border text-center h-100">
                                    <Card.Body className="p-3">
                                        <h4 className="text-primary mb-0">12+</h4>
                                        <small className="text-secondary">validated ideas/mo</small>
                                    </Card.Body>
                                </Card>
                            </motion.div>
                        </Col>
                        <Col md={3} xs={6}>
                            <motion.div variants={statsVariants} whileHover={{ y: -5, scale: 1.02, transition: { duration: 0.2 } }}>
                                <Card className="border text-center h-100">
                                    <Card.Body className="p-3">
                                        <h4 className="text-primary mb-0"><span className="me-1"><i className="ri-arrow-up-line"></i></span>18%</h4>
                                        <small className="text-secondary">avg. conv. lift</small>
                                    </Card.Body>
                                </Card>
                            </motion.div>
                        </Col>
                        <Col md={3} xs={6}>
                            <motion.div variants={statsVariants} whileHover={{ y: -5, scale: 1.02, transition: { duration: 0.2 } }}>
                                <Card className="border text-center h-100">
                                    <Card.Body className="p-3">
                                        <h4 className="text-primary mb-0">NPS 9.1</h4>
                                        <small className="text-secondary">client satisfaction</small>
                                    </Card.Body>
                                </Card>
                            </motion.div>
                        </Col>
                    </Row>
                </motion.div>
            </Container>
        </section>
    )
}

export default HeroSection;